/**
 * Route → browser tab title map.
 *
 * The `RouteTitleSync` component (mounted once in App) walks every
 * navigation, looks up the matching path here, and writes
 * `document.title`. Pages that want a *dynamic* title (e.g. study
 * name) can call `useDocumentTitle("Study: Q4 Demand")` directly —
 * the hook overrides whatever this map set, and resets on unmount.
 *
 * Patterns matter: longer paths are matched first so
 * `/admin/audit-log` doesn't get swallowed by `/admin`. The matcher
 * normalises trailing slashes and supports `:param` segments.
 */

export interface PageTitleEntry {
  /** Path pattern. `:param` segments match any non-slash chunk. */
  path: string;
  /** Display title (gets the global " · UpSynQ" suffix). */
  title: string;
}

export const PAGE_TITLES: PageTitleEntry[] = [
  // Auth / onboarding
  { path: "/login",             title: "Sign in" },
  { path: "/register",          title: "Sign up" },
  { path: "/signup-success",    title: "Check your email" },
  { path: "/verify-email",      title: "Verify email" },
  { path: "/forgot-password",   title: "Forgot password" },
  { path: "/reset-password",    title: "Reset password" },
  { path: "/accept-invitation", title: "Accept invitation" },

  // Core
  { path: "/",                  title: "SynqAI" },
  { path: "/study",             title: "Studies" },
  { path: "/foundry",           title: "Data Foundry" },
  { path: "/jobs",              title: "Data Jobs" },
  { path: "/data-jobs",         title: "Data Jobs" },
  { path: "/add-lookup",        title: "Add Lookup" },
  { path: "/entity-preview/:entityName", title: "Entity Preview" },

  // Planning workflows
  { path: "/demand-forecasting",    title: "Demand Forecasting" },
  { path: "/assortment-planning",   title: "Assortment Planning" },
  { path: "/inventory-optimization", title: "Inventory Optimization" },
  { path: "/replenishment-planning", title: "Replenishment Planning" },
  { path: "/production-planning",   title: "Production Planning" },
  { path: "/raw-material-planning", title: "Raw Material Planning" },
  { path: "/logistics-optimization", title: "Logistics Optimization" },
  { path: "/opex-planning",         title: "Opex Planning" },
  { path: "/capex-planning",        title: "Capex Planning" },
  { path: "/project-prioritization", title: "Project Prioritization" },
  { path: "/procurement-planning",  title: "Procurement Planning" },
  { path: "/target-sales-planning", title: "Target Sales Planning" },
  { path: "/tsp-results",           title: "Target Sales Results" },

  // Admin
  { path: "/admin/studies",     title: "Admin · Studies" },
  { path: "/admin/audit-log",   title: "Admin · Audit Log" },
  { path: "/admin/roles",       title: "Admin · Roles" },
  { path: "/admin/users",       title: "Admin · Users" },
  { path: "/admin",             title: "Admin" },

  // Misc
  { path: "/settings",          title: "Settings" },
  { path: "/build",             title: "Build" },
  { path: "/reports",           title: "Reports" },
  { path: "/workflows",         title: "Workflows" },
  { path: "/workflow/:id",      title: "Workflow Run" },
  { path: "/dashboard",         title: "Dashboard" },
];

/** Compile a `:param` pattern into a regex once at module-load. */
const COMPILED = PAGE_TITLES.map((entry) => {
  const re = new RegExp(
    "^" + entry.path.replace(/:[A-Za-z_]+/g, "[^/]+") + "/?$",
  );
  return { ...entry, re };
});

// Sort by descending literal length so /admin/audit-log wins over /admin.
COMPILED.sort((a, b) => b.path.length - a.path.length);

export function titleForPath(pathname: string): string | null {
  for (const entry of COMPILED) {
    if (entry.re.test(pathname)) return entry.title;
  }
  return null;
}
